import React, { useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Descriptions,
  Modal,
  Result,
  Skeleton,
  Space,
  Tag,
  Typography,
  message,
} from 'antd';
import { ReloadOutlined, DisconnectOutlined, QrcodeOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { FaWhatsapp } from 'react-icons/fa';
import DashboardPageWrapper from '../../components/layout/DashboardPageWrapper';
import { useAuth } from '../../contexts/AuthContext';
import { PERMISSIONS } from '../../config/permissions';
import whapiService from '../../services/whapiService';

const { Title, Paragraph, Text } = Typography;

const STATUS_GROUP_BADGE = {
  connected: { status: 'success', color: 'green', label: 'Connected' },
  awaiting_qr: { status: 'warning', color: 'gold', label: 'Awaiting QR scan' },
  transient: { status: 'processing', color: 'blue', label: 'Connecting' },
  error: { status: 'error', color: 'red', label: 'Disconnected' },
  unknown: { status: 'default', color: 'default', label: 'Unknown' },
};

const formatUptime = (seconds) => {
  if (typeof seconds !== 'number' || seconds < 0) return '—';
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
};

const formatLastChecked = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleTimeString();
  } catch {
    return iso;
  }
};

export default function WhatsAppIntegrationPage() {
  const { hasPermission, isSuperAdmin } = useAuth();
  const canManage = isSuperAdmin || hasPermission(PERMISSIONS.INTEGRATION_WHATSAPP_MANAGE);

  const [status, setStatus] = useState(null);
  const [chatbotConfig, setChatbotConfig] = useState(null);
  const [chatbotConfigLoading, setChatbotConfigLoading] = useState(false);
  const [webhookConfigureLoading, setWebhookConfigureLoading] = useState(false);
  const [webhookTestLoading, setWebhookTestLoading] = useState(false);
  const [statusError, setStatusError] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);

  const [qrUrl, setQrUrl] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [qrError, setQrError] = useState(null);

  const loadStatus = async () => {
    setStatusLoading(true);
    setStatusError(null);
    try {
      const res = await whapiService.getStatus();
      setStatus(res?.data ?? null);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to fetch WhatsApp channel status.';
      setStatusError(msg);
      setStatus(null);
    } finally {
      setStatusLoading(false);
      setHasFetched(true);
    }
  };

  const loadChatbotConfig = async () => {
    setChatbotConfigLoading(true);
    try {
      const res = await whapiService.getChatbotConfig();
      setChatbotConfig(res?.data ?? null);
    } catch {
      setChatbotConfig(null);
    } finally {
      setChatbotConfigLoading(false);
    }
  };

  const handleConfigureWebhook = async () => {
    setWebhookConfigureLoading(true);
    try {
      const res = await whapiService.configureChatbotWebhook();
      message.success(res?.message || 'Webhook configured in Whapi.');
      await loadChatbotConfig();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to configure webhook in Whapi.';
      message.error(msg);
    } finally {
      setWebhookConfigureLoading(false);
    }
  };

  const handleTestWebhook = async () => {
    setWebhookTestLoading(true);
    try {
      const res = await whapiService.testChatbotWebhook();
      message.success(res?.message || 'Webhook test sent via Whapi.');
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Webhook test failed.';
      message.error(msg);
    } finally {
      setWebhookTestLoading(false);
    }
  };

  useEffect(() => {
    loadChatbotConfig();
  }, []);

  const loadQr = async () => {
    setQrLoading(true);
    setQrError(null);
    setQrUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    try {
      const url = await whapiService.fetchQrObjectUrl();
      setQrUrl(url);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to fetch QR code.';
      setQrError(msg);
    } finally {
      setQrLoading(false);
    }
  };

  // No status fetch on mount — admin must click "Refresh status" explicitly.
  // Only effect on mount/unmount is to revoke any QR object URL on unmount.
  useEffect(() => {
    return () => {
      setQrUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    };
  }, []);

  // Auto-load QR whenever the channel is awaiting scan and we don't already have one.
  useEffect(() => {
    if (status?.status_group === 'awaiting_qr' && !qrUrl && !qrLoading) {
      loadQr();
    }
    if (status && status.status_group !== 'awaiting_qr' && qrUrl) {
      URL.revokeObjectURL(qrUrl);
      setQrUrl(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status?.status_group]);

  const handleLogout = () => {
    Modal.confirm({
      title: 'Disconnect WhatsApp channel?',
      icon: <ExclamationCircleOutlined />,
      content:
        'This will end the current WhatsApp session. All outbound alerts will stop until a new number is paired via QR code.',
      okText: 'Disconnect',
      okButtonProps: { danger: true },
      cancelText: 'Cancel',
      onOk: async () => {
        setLogoutLoading(true);
        try {
          const res = await whapiService.logout();
          message.success(res?.message || 'WhatsApp channel disconnected.');
          await loadStatus();
        } catch (err) {
          const msg = err?.response?.data?.message || err?.message || 'Failed to disconnect channel.';
          message.error(msg);
        } finally {
          setLogoutLoading(false);
        }
      },
    });
  };

  const handleReconnect = () => {
    Modal.confirm({
      title: 'Reconnect WhatsApp channel?',
      icon: <ExclamationCircleOutlined />,
      content:
        'This will log out the current session and show a new QR code so you can pair the number again.',
      okText: 'Reconnect',
      cancelText: 'Cancel',
      onOk: async () => {
        setLogoutLoading(true);
        try {
          await whapiService.logout();
          message.success('Channel reset. Fetching new QR code...');
          await loadStatus();
        } catch (err) {
          const msg = err?.response?.data?.message || err?.message || 'Failed to reconnect channel.';
          message.error(msg);
        } finally {
          setLogoutLoading(false);
        }
      },
    });
  };

  const renderHeader = () => {
    const group = status?.status_group ?? 'unknown';
    const badge = STATUS_GROUP_BADGE[group];

    return (
      <Card>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <FaWhatsapp size={36} color="#25D366" />
            <div>
              <Title level={4} style={{ marginBottom: 4 }}>
                WhatsApp Channel (Whapi Cloud)
              </Title>
              <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                Single number used to send price alerts, transfer status, and other partner notifications.
              </Paragraph>
            </div>
          </div>
          <Space wrap>
            {hasFetched && (
              <>
                <Badge status={badge.status} text={<Text strong>{badge.label}</Text>} />
                {status?.status_text && status.status_text !== badge.label && (
                  <Tag color={badge.color}>{status.status_text}</Tag>
                )}
              </>
            )}
            <Button
              type={hasFetched ? 'default' : 'primary'}
              icon={<ReloadOutlined />}
              onClick={loadStatus}
              loading={statusLoading}
            >
              Refresh status
            </Button>
          </Space>
        </div>

        {hasFetched && status && (
          <Descriptions
            size="small"
            column={{ xs: 1, sm: 2, md: 3 }}
            style={{ marginTop: 16 }}
            colon={false}
          >
            <Descriptions.Item label="Connected number">
              {status.connected_phone ? `+${status.connected_phone}` : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Account name">
              {status.connected_name || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Channel ID">
              {status.channel_id || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Uptime">
              {formatUptime(status.uptime_seconds)}
            </Descriptions.Item>
            <Descriptions.Item label="Whapi version">
              {status.version || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Last checked">
              {formatLastChecked(status.last_checked_at)}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Card>
    );
  };

  const renderConnectedActions = () => (
    <Card title="Manage connection">
      <Paragraph>
        The channel is connected and operational. Use the action below only when you need to switch the
        WhatsApp number being used by the platform.
      </Paragraph>
      <Button
        danger
        icon={<DisconnectOutlined />}
        onClick={handleLogout}
        loading={logoutLoading}
        disabled={!canManage}
      >
        Disconnect &amp; re-pair number
      </Button>
      {!canManage && (
        <Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
          You need the <Text code>INTEGRATION_WHATSAPP_MANAGE</Text> permission to disconnect.
        </Paragraph>
      )}
    </Card>
  );

  const renderQrCard = () => (
    <Card
      title={
        <Space>
          <QrcodeOutlined />
          <span>Scan QR to link a WhatsApp number</span>
        </Space>
      }
      extra={
        <Button onClick={loadQr} loading={qrLoading} icon={<ReloadOutlined />}>
          Refresh QR
        </Button>
      }
    >
      <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-start">
        <div
          style={{
            width: 280,
            height: 280,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#fafafa',
            border: '1px solid #f0f0f0',
            borderRadius: 8,
          }}
        >
          {qrLoading && <Skeleton.Image active style={{ width: 240, height: 240 }} />}
          {!qrLoading && qrUrl && (
            <img src={qrUrl} alt="WhatsApp pairing QR" width={260} height={260} />
          )}
          {!qrLoading && !qrUrl && qrError && (
            <Text type="danger" style={{ padding: 16, textAlign: 'center' }}>
              {qrError}
            </Text>
          )}
        </div>

        <div style={{ flex: 1 }}>
          <Title level={5}>How to pair</Title>
          <ol style={{ paddingLeft: 18, marginBottom: 12 }}>
            <li>Open WhatsApp on the phone holding the business number.</li>
            <li>Go to <Text strong>Settings → Linked devices → Link a device</Text>.</li>
            <li>Scan the QR code on the left.</li>
            <li>Click <Text strong>Refresh status</Text> at the top after the phone confirms the link.</li>
          </ol>
          <Alert
            type="info"
            showIcon
            message="The QR code is short-lived. If it expires, click Refresh QR to get a new one."
          />
        </div>
      </div>
    </Card>
  );

  const renderTransientCard = () => (
    <Card>
      <Result
        status="info"
        title={`Channel is ${status?.status_text || 'initialising'}`}
        subTitle="The channel is establishing a connection with WhatsApp. This usually takes a few seconds. Click Refresh status above to check again."
      />
    </Card>
  );

  const renderErrorCard = () => {
    const text = status?.status_text || 'ERROR';
    const explanations = {
      SYNC_ERROR:
        'WhatsApp and the API have fallen out of sync. Sending may still work but reading messages will fail. Reconnecting fixes this.',
      ERROR: 'The channel encountered a connection error. Try reconnecting.',
      STOP: 'The channel is stopped (subscription / billing / manual deactivation). Check the Whapi dashboard.',
      NOT_INIT: 'The channel has not been initialised yet. Try reconnecting.',
    };

    return (
      <Card title={`Channel state: ${text}`}>
        <Paragraph>{explanations[text] || 'The channel is not connected.'}</Paragraph>
        <Button
          type="primary"
          danger
          icon={<DisconnectOutlined />}
          onClick={handleReconnect}
          loading={logoutLoading}
          disabled={!canManage}
        >
          Reset &amp; reconnect
        </Button>
        {!canManage && (
          <Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
            You need the <Text code>INTEGRATION_WHATSAPP_MANAGE</Text> permission to reset the channel.
          </Paragraph>
        )}
      </Card>
    );
  };

  const renderChatbotWebhookCard = () => {
    if (chatbotConfigLoading && !chatbotConfig) {
      return (
        <Card title="Price chatbot webhook">
          <Skeleton active paragraph={{ rows: 3 }} />
        </Card>
      );
    }

    if (!chatbotConfig) return null;

    const targets = chatbotConfig.webhook_targets?.length
      ? chatbotConfig.webhook_targets
      : [];

    const whapiInSync = chatbotConfig.dual_webhook_mode
      ? chatbotConfig.whapi_all_targets_in_sync
      : (
        chatbotConfig.whapi_webhook_configured
        && chatbotConfig.whapi_webhook_url_matches
        && (
          chatbotConfig.whapi_authorization_configured
          || chatbotConfig.whapi_authorization_header_present
        )
      );

    return (
      <Card
        title="Price chatbot webhook"
        extra={
          <Button
            icon={<ReloadOutlined />}
            onClick={loadChatbotConfig}
            loading={chatbotConfigLoading}
          >
            Refresh
          </Button>
        }
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <div>
            <Text type="secondary">Chatbot pipeline: </Text>
            <Tag color={chatbotConfig.chatbot_enabled ? 'green' : 'default'}>
              {chatbotConfig.chatbot_enabled ? 'Enabled' : 'Disabled'}
            </Tag>
            <Text type="secondary" style={{ marginLeft: 8 }}>
              (WHATSAPP_CHATBOT_ENABLED)
            </Text>
          </div>

          {chatbotConfig.dual_webhook_mode && (
            <Alert
              type="info"
              showIcon
              message="Dual webhook mode (one Whapi channel → UAT + prod)"
              description="Whapi sends every message to both URLs. Each environment replies only for chats on its own allowlist. Do not enable the same group on UAT and prod — that causes duplicate replies."
            />
          )}

          {targets.length > 0 ? (
            <>
              <div>
                <Text strong>
                  {chatbotConfig.dual_webhook_mode ? 'Webhook targets' : 'Target webhook URL'}
                </Text>
                <Space direction="vertical" size="small" style={{ width: '100%', marginTop: 8 }}>
                  {targets.map((target) => (
                    <div key={target.url}>
                      <Space wrap>
                        {target.is_local && <Tag color="blue">This env</Tag>}
                        <Text code copyable>{target.url}</Text>
                        <Tag color={target.whapi_webhook_configured ? 'green' : 'default'}>
                          {target.whapi_webhook_configured ? 'In Whapi' : 'Missing in Whapi'}
                        </Tag>
                        {target.whapi_webhook_configured && (
                          <Tag color={target.whapi_webhook_url_matches ? 'green' : 'orange'}>
                            {target.whapi_webhook_url_matches ? 'URL OK' : 'URL mismatch'}
                          </Tag>
                        )}
                        {!target.bearer_configured && (
                          <Tag color="warning">Bearer not set</Tag>
                        )}
                      </Space>
                    </div>
                  ))}
                </Space>
              </div>

              {chatbotConfig.dual_webhook_mode ? (
                <div>
                  <Space wrap>
                    <Tag color={chatbotConfig.whapi_all_targets_configured ? 'blue' : 'default'}>
                      {chatbotConfig.whapi_all_targets_configured
                        ? 'All targets in Whapi'
                        : 'Some targets missing in Whapi'}
                    </Tag>
                    {whapiInSync && <Tag color="success">All in sync</Tag>}
                  </Space>
                </div>
              ) : (
                <div>
                  <Space wrap>
                    <Tag color={chatbotConfig.whapi_webhook_configured ? 'blue' : 'default'}>
                      {chatbotConfig.whapi_webhook_configured ? 'Webhook set' : 'Not configured'}
                    </Tag>
                    {whapiInSync && <Tag color="success">In sync</Tag>}
                  </Space>
                </div>
              )}

              {chatbotConfig.whapi_settings_fetch_error && (
                <Alert
                  type="warning"
                  showIcon
                  message="Could not read Whapi channel settings"
                  description={chatbotConfig.whapi_settings_fetch_error}
                />
              )}

              {!chatbotConfig.env_ready_for_whapi_configure && (
                <Alert
                  type="warning"
                  showIcon
                  message="API environment incomplete"
                  description="Set WHAPI_CHATBOT_WEBHOOK_TARGETS (or API_PUBLIC_BASE_URL) and bearer token(s) before configuring Whapi."
                />
              )}

              <Space wrap>
                <Button
                  type="primary"
                  onClick={handleConfigureWebhook}
                  loading={webhookConfigureLoading}
                  disabled={!canManage || !chatbotConfig.env_ready_for_whapi_configure}
                >
                  {chatbotConfig.dual_webhook_mode
                    ? 'Configure all webhooks in Whapi'
                    : 'Configure webhook in Whapi'}
                </Button>
                <Button
                  onClick={handleTestWebhook}
                  loading={webhookTestLoading}
                  disabled={!canManage || !chatbotConfig.webhook_url}
                >
                  Test this env webhook
                </Button>
              </Space>

              <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                {chatbotConfig.dual_webhook_mode
                  ? 'Configure all pushes every target URL to Whapi (replaces all messages POST hooks with the list below).'
                  : 'Configure webhook adds/updates this environment only — other messages webhooks already in Whapi (e.g. prod) are kept.'}
                {' '}Each URL uses <Text code>Authorization: Bearer …</Text>. Use disjoint allowlists per environment so only one API replies per chat.
              </Paragraph>
            </>
          ) : (
            <Alert
              type="info"
              showIcon
              message="Set API_PUBLIC_BASE_URL or WHAPI_CHATBOT_WEBHOOK_TARGETS"
              description="The public HTTPS webhook URL(s) are required to register with Whapi."
            />
          )}

          {!canManage && (
            <Paragraph type="secondary" style={{ marginBottom: 0 }}>
              You need <Text code>INTEGRATION_WHATSAPP_MANAGE</Text> to configure or test the webhook.
            </Paragraph>
          )}
        </Space>
      </Card>
    );
  };

  const renderBody = () => {
    if (statusLoading && !status) {
      return (
        <Card>
          <Skeleton active paragraph={{ rows: 4 }} />
        </Card>
      );
    }

    if (!hasFetched && !status && !statusError) {
      return (
        <Card>
          <Result
            icon={<ReloadOutlined style={{ color: '#1677ff' }} />}
            title="Click Refresh status to check the WhatsApp channel"
            subTitle="The channel state isn't fetched automatically. Use the Refresh status button above whenever you want a live check."
            extra={
              <Button type="primary" icon={<ReloadOutlined />} onClick={loadStatus}>
                Refresh status
              </Button>
            }
          />
        </Card>
      );
    }

    if (statusError) {
      return (
        <Alert
          type="error"
          showIcon
          message="Unable to reach the WhatsApp service"
          description={statusError}
          action={
            <Button onClick={loadStatus} icon={<ReloadOutlined />}>
              Retry
            </Button>
          }
        />
      );
    }

    if (!status) return null;

    switch (status.status_group) {
      case 'connected':
        return renderConnectedActions();
      case 'awaiting_qr':
        return renderQrCard();
      case 'transient':
        return renderTransientCard();
      case 'error':
        return renderErrorCard();
      default:
        return (
          <Alert
            type="warning"
            showIcon
            message={`Unknown channel state: ${status.status_text}`}
            description="Click Refresh status to fetch the latest state."
          />
        );
    }
  };

  return (
    <DashboardPageWrapper>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {renderHeader()}
        {renderChatbotWebhookCard()}
        {renderBody()}
      </Space>
    </DashboardPageWrapper>
  );
}
